import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClosingContract,
  SmoUlClosingStatus,
  closeClosingContracts,
  getClosingContracts,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { ClosingStatusIcon, closingStatusLabel } from '../components/ClosingStatusIcon';
import { formatDeadlineCompact, formatDeadlineRelativeTo } from '../utils/deadline';
import { CONTRACT_STATUSES } from '../utils/contractStatus';
import '../styles/DataPage.css';

type ClosingContractSortKey =
  | 'contractor'
  | 'contract_number'
  | 'address'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'expense_closing_date'
  | 'overdue'
  | 'total_amount'
  | 'contract_status'
  | 'expenses_closed'
  | 'fully_paid';

type ClosingContractFilterMenuKey =
  | 'contractor'
  | 'contract_number'
  | 'manager'
  | 'project_lead'
  | 'overdue'
  | 'contract_status'
  | 'expenses_closed'
  | 'fully_paid'
  | null;

const CIRCLE_FILTER_OPTIONS: SmoUlClosingStatus[] = ['closed', 'partial', 'open'];
const OVERDUE_FILTER_OPTIONS = ['overdue', 'on_time', 'none'];

const STATUS_SORT_RANK: Record<SmoUlClosingStatus, number> = {
  na: 0,
  open: 1,
  partial: 2,
  waiting: 3,
  closed: 4,
};

function contractLevel(contract: ClosingContract): number {
  return contract.hierarchy_level ?? 1;
}

function isMainContract(contract: ClosingContract): boolean {
  return contractLevel(contract) === 1;
}

function contractDetailPath(contract: ClosingContract): string {
  const mainId = contract.main_contract_id || contract.id;
  return `/contracts/${mainId}`;
}

function formatContractAddressCell(contract: Pick<ClosingContract, 'address' | 'cadastral_number'>): string {
  const address = contract.address?.trim() ?? '';
  const cadastral = contract.cadastral_number?.trim() ?? '';
  if (address && cadastral) return `${address}; к.н.: ${cadastral}`;
  if (address) return address;
  if (cadastral) return `к.н.: ${cadastral}`;
  return '—';
}

function overdueFilterLabel(value: string): string {
  if (value === 'overdue') return 'Просрочено';
  if (value === 'on_time') return 'Не просрочено';
  return 'Нет срока';
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

const ClosingContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [contracts, setContracts] = useState<ClosingContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ClosingContractFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterContractorIds, setFilterContractorIds] = useState<string[]>([]);
  const [filterContractNumbers, setFilterContractNumbers] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [filterExpenseFrom, setFilterExpenseFrom] = useState('');
  const [filterExpenseTo, setFilterExpenseTo] = useState('');
  const [filterOverdue, setFilterOverdue] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterExpenses, setFilterExpenses] = useState<string[]>([]);
  const [filterPaid, setFilterPaid] = useState<string[]>([]);

  const [sortState, setSortState] = useState<SortState<ClosingContractSortKey>>({
    key: 'contract_number',
    direction: 'desc',
  });

  const canOpenPage = can('CONTRACTS_CLOSING', 'CREATE');
  const canCloseFromAccounting = canOpenPage;
  const tableColSpan = (canCloseFromAccounting ? 1 : 0) + 13;

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setContracts([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getClosingContracts();
      setContracts(data.contracts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setContracts([]);
    }
  }, [canOpenPage, fetchData]);

  const clearFilters = (): void => {
    setSearchText('');
    setFilterContractorIds([]);
    setFilterContractNumbers([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setFilterStartFrom('');
    setFilterStartTo('');
    setFilterEndFrom('');
    setFilterEndTo('');
    setFilterExpenseFrom('');
    setFilterExpenseTo('');
    setFilterOverdue([]);
    setFilterStatuses([]);
    setFilterExpenses([]);
    setFilterPaid([]);
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterContractorIds.length > 0 ||
    filterContractNumbers.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    Boolean(filterStartFrom.trim()) ||
    Boolean(filterStartTo.trim()) ||
    Boolean(filterEndFrom.trim()) ||
    Boolean(filterEndTo.trim()) ||
    Boolean(filterExpenseFrom.trim()) ||
    Boolean(filterExpenseTo.trim()) ||
    filterOverdue.length > 0 ||
    filterStatuses.length > 0 ||
    filterExpenses.length > 0 ||
    filterPaid.length > 0;

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const mains = useMemo(() => contracts.filter(isMainContract), [contracts]);
  const dsByMainId = useMemo(() => {
    const map = new Map<number, ClosingContract[]>();
    for (const contract of contracts) {
      if (isMainContract(contract)) continue;
      const mainId = contract.main_contract_id || contract.id;
      const list = map.get(mainId) ?? [];
      list.push(contract);
      map.set(mainId, list);
    }
    for (const list of map.values()) {
      list.sort((left, right) =>
        left.contract_number.localeCompare(right.contract_number, 'ru', { numeric: true }),
      );
    }
    return map;
  }, [contracts]);

  const contractorFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const contract of mains) {
      set.add(String(contract.contractor_id));
    }
    return [...set].sort((a, b) => {
      const left = mains.find((item) => String(item.contractor_id) === a)?.contractor_name ?? a;
      const right = mains.find((item) => String(item.contractor_id) === b)?.contractor_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [mains]);

  const numberFilterOptions = useMemo(() => {
    const set = new Set(contracts.map((contract) => contract.contract_number));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [contracts]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const contract of mains) {
      if (contract.manager_id != null) {
        set.add(String(contract.manager_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = mains.find((item) => String(item.manager_id) === a)?.manager_name ?? a;
      const right = mains.find((item) => String(item.manager_id) === b)?.manager_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [mains]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const contract of mains) {
      if (contract.project_lead_id != null) {
        set.add(String(contract.project_lead_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = mains.find((item) => String(item.project_lead_id) === a)?.project_lead_name ?? a;
      const right = mains.find((item) => String(item.project_lead_id) === b)?.project_lead_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [mains]);

  const getContractorLabel = useCallback(
    (id: string): string =>
      mains.find((contract) => String(contract.contractor_id) === id)?.contractor_name ?? id,
    [mains],
  );

  const getManagerLabel = useCallback(
    (id: string): string =>
      mains.find((contract) => String(contract.manager_id) === id)?.manager_name ?? id,
    [mains],
  );

  const getLeadLabel = useCallback(
    (id: string): string =>
      mains.find((contract) => String(contract.project_lead_id) === id)?.project_lead_name ?? id,
    [mains],
  );

  const getDeadline = useCallback((contract: ClosingContract) => {
    return formatDeadlineRelativeTo(contract.end_date, contract.expense_closing_date);
  }, []);

  const overdueFilterValue = useCallback(
    (contract: ClosingContract): string => {
      const deadline = getDeadline(contract);
      if (!deadline.label) return 'none';
      return deadline.overdue ? 'overdue' : 'on_time';
    },
    [getDeadline],
  );

  const searchableText = useCallback((contract: ClosingContract): string => {
    const deadline = isMainContract(contract) ? getDeadline(contract) : null;
    return [
      contract.contractor_name,
      contract.contract_number,
      formatContractAddressCell(contract),
      contract.manager_name,
      contract.project_lead_name,
      contract.start_date ? formatDateShort(contract.start_date) : '',
      contract.end_date ? formatDateShort(contract.end_date) : '',
      contract.expense_closing_date ? formatDateShort(contract.expense_closing_date) : '',
      deadline?.label ? deadline.label.toLowerCase() : '',
      deadline ? formatDeadlineCompact(deadline) : '',
      formatContractSumDisplay(contract.total_amount ?? 0),
      contract.contract_status,
      closingStatusLabel(contract.expenses_closed.status),
      closingStatusLabel(contract.fully_paid.status),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }, [getDeadline]);

  const filteredMains = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return mains.filter((contract) => {
      const family = [contract, ...(dsByMainId.get(contract.id) ?? [])];
      const matchesSearch = !text || family.some((item) => searchableText(item).includes(text));
      const matchesContractor =
        filterContractorIds.length === 0 || filterContractorIds.includes(String(contract.contractor_id));
      const matchesNumber =
        filterContractNumbers.length === 0 ||
        family.some((item) => filterContractNumbers.includes(item.contract_number));
      const matchesManager =
        filterManagerIds.length === 0 || filterManagerIds.includes(String(contract.manager_id || ''));
      const matchesLead =
        filterLeadIds.length === 0 || filterLeadIds.includes(String(contract.project_lead_id || ''));
      const matchesStart = dateInRange(contract.start_date, filterStartFrom, filterStartTo);
      const matchesEnd = dateInRange(contract.end_date, filterEndFrom, filterEndTo);
      const matchesExpenseClosing = dateInRange(
        contract.expense_closing_date,
        filterExpenseFrom,
        filterExpenseTo,
      );
      const matchesOverdue =
        filterOverdue.length === 0 || filterOverdue.includes(overdueFilterValue(contract));
      const matchesStatus =
        filterStatuses.length === 0 || filterStatuses.includes(contract.contract_status || '');
      const matchesExpenses =
        filterExpenses.length === 0 || filterExpenses.includes(contract.expenses_closed.status);
      const matchesPaid =
        filterPaid.length === 0 || filterPaid.includes(contract.fully_paid.status);

      return (
        matchesSearch &&
        matchesContractor &&
        matchesNumber &&
        matchesManager &&
        matchesLead &&
        matchesStart &&
        matchesEnd &&
        matchesExpenseClosing &&
        matchesOverdue &&
        matchesStatus &&
        matchesExpenses &&
        matchesPaid
      );
    });
  }, [
    mains,
    dsByMainId,
    searchText,
    filterContractorIds,
    filterContractNumbers,
    filterManagerIds,
    filterLeadIds,
    filterStartFrom,
    filterStartTo,
    filterEndFrom,
    filterEndTo,
    filterExpenseFrom,
    filterExpenseTo,
    filterOverdue,
    filterStatuses,
    filterExpenses,
    filterPaid,
    searchableText,
    overdueFilterValue,
  ]);

  const sortedContracts = useMemo(() => {
    const getSortValue = (
      contract: ClosingContract,
      key: ClosingContractSortKey,
    ): string | number | undefined => {
      const deadline = getDeadline(contract);
      const values: Record<ClosingContractSortKey, string | number | undefined> = {
        contractor: contract.contractor_name,
        contract_number: contract.contract_number,
        address: formatContractAddressCell(contract),
        manager: contract.manager_name,
        projectLead: contract.project_lead_name,
        start_date: contract.start_date ?? undefined,
        end_date: contract.end_date ?? undefined,
        expense_closing_date: contract.expense_closing_date ?? undefined,
        overdue: contract.end_date ?? undefined,
        total_amount: contract.total_amount ?? 0,
        contract_status: contract.contract_status,
        expenses_closed: STATUS_SORT_RANK[contract.expenses_closed.status],
        fully_paid: STATUS_SORT_RANK[contract.fully_paid.status],
      };
      if (key === 'overdue') {
        return deadline.overdue
          ? -(deadline.months ?? 0) * 30 - (deadline.days ?? 0)
          : (deadline.months ?? 0) * 30 + (deadline.days ?? 0);
      }
      return values[key];
    };

    const { key: sortKey, direction: sortDirection } = sortState;
    const sortedMains = [...filteredMains].sort((left, right) =>
      compareValues(getSortValue(left, sortKey), getSortValue(right, sortKey), sortDirection),
    );

    const result: ClosingContract[] = [];
    for (const main of sortedMains) {
      result.push(main);
      result.push(...(dsByMainId.get(main.id) ?? []));
    }
    return result;
  }, [filteredMains, dsByMainId, sortState, getDeadline]);

  const visibleIds = useMemo(
    () => sortedContracts.filter(isMainContract).map((contract) => contract.id),
    [sortedContracts],
  );
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const totalFilteredAmount = useMemo(
    () => sortedContracts.reduce((sum, contract) => sum + (Number(contract.total_amount) || 0), 0),
    [sortedContracts],
  );

  useEffect(() => {
    const knownIds = new Set(mains.map((contract) => contract.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => knownIds.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [mains]);

  const handleSort = (key: ClosingContractSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const toggleSelectAllVisible = (): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRowSelected = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCloseFromAccounting = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Выберите хотя бы один договор 1 уровня.');
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const created = await closeClosingContracts({ contract_ids: ids });
      setSuccess(`Создано записей о закрытии: ${created}`);
      setSelectedIds(new Set());
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при закрытии договоров');
    } finally {
      setSaving(false);
    }
  };

  const colgroup = (
    <colgroup>
      {canCloseFromAccounting ? (
        <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--check" />
      ) : null}
      <col className="contracts-page__colgrid contracts-page__colgrid--contractor" />
      <col className="contracts-page__colgrid contracts-page__colgrid--number" />
      <col className="contracts-page__colgrid contracts-page__colgrid--address" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
      <col className="contracts-page__colgrid contracts-page__colgrid--sum" />
      <col className="contracts-page__colgrid contracts-page__colgrid--status" />
      <col className="contracts-page__colgrid closing-contracts-page__colgrid--check" />
      <col className="contracts-page__colgrid closing-contracts-page__colgrid--check" />
    </colgroup>
  );

  return (
    <div className="data-page contracts-page in-progress-contracts-page in-progress-smo-ul-page closing-tasks-page closing-contracts-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице (требуется CONTRACTS_CLOSING CREATE).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Это договоры подлежащие закрытию от Бухгалтерии</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success ? <div className="success-message">{success}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorFilterOptions}
                      selected={filterContractorIds}
                      onSelectedChange={setFilterContractorIds}
                      formatOption={getContractorLabel}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Номер договора"
                      options={numberFilterOptions}
                      selected={filterContractNumbers}
                      onSelectedChange={setFilterContractNumbers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract_number'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_number' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Менеджер"
                      options={managerFilterOptions}
                      selected={filterManagerIds}
                      onSelectedChange={setFilterManagerIds}
                      formatOption={getManagerLabel}
                      isOpen={filterMenuOpen === 'manager'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="РП"
                      options={projectLeadFilterOptions}
                      selected={filterLeadIds}
                      onSelectedChange={setFilterLeadIds}
                      formatOption={getLeadLabel}
                      isOpen={filterMenuOpen === 'project_lead'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Начало</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartFrom}
                        onChange={(e) => setFilterStartFrom(e.target.value)}
                        title="Начало с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartTo}
                        onChange={(e) => setFilterStartTo(e.target.value)}
                        title="Начало по"
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Срок</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndFrom}
                        onChange={(e) => setFilterEndFrom(e.target.value)}
                        title="Срок с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndTo}
                        onChange={(e) => setFilterEndTo(e.target.value)}
                        title="Срок по"
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Факт закрытие расходов</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterExpenseFrom}
                        onChange={(e) => setFilterExpenseFrom(e.target.value)}
                        title="Факт закрытие расходов с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterExpenseTo}
                        onChange={(e) => setFilterExpenseTo(e.target.value)}
                        title="Факт закрытие расходов по"
                      />
                    </div>
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Просрочка"
                      options={OVERDUE_FILTER_OPTIONS}
                      selected={filterOverdue}
                      onSelectedChange={setFilterOverdue}
                      formatOption={overdueFilterLabel}
                      isOpen={filterMenuOpen === 'overdue'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'overdue' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статус"
                      options={[...CONTRACT_STATUSES]}
                      selected={filterStatuses}
                      onSelectedChange={setFilterStatuses}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract_status'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract_status' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Полная оплата"
                      options={CIRCLE_FILTER_OPTIONS}
                      selected={filterPaid}
                      onSelectedChange={setFilterPaid}
                      formatOption={(v) => closingStatusLabel(v as SmoUlClosingStatus)}
                      isOpen={filterMenuOpen === 'fully_paid'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'fully_paid' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Все этапы закрыты бухгалтерией"
                      options={CIRCLE_FILTER_OPTIONS}
                      selected={filterExpenses}
                      onSelectedChange={setFilterExpenses}
                      formatOption={(v) => closingStatusLabel(v as SmoUlClosingStatus)}
                      isOpen={filterMenuOpen === 'expenses_closed'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'expenses_closed' : null)}
                    />
                  </div>
                </div>
              </aside>
            )}

            <section className="table-content">
              {canCloseFromAccounting ? (
                <div className="closing-tasks-page__toolbar">
                  <button
                    type="button"
                    className="closing-tasks-page__action-button"
                    onClick={() => void handleCloseFromAccounting()}
                    disabled={saving || selectedIds.size === 0}
                  >
                    Закрыть от Бухгалтерии
                  </button>
                </div>
              ) : null}
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          {canCloseFromAccounting ? (
                            <th className="closing-tasks-page__th-check" rowSpan={2}>
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someVisibleSelected;
                                }}
                                onChange={toggleSelectAllVisible}
                                disabled={visibleIds.length === 0}
                                aria-label="Выбрать все"
                                title="Выбрать все"
                              />
                            </th>
                          ) : null}
                          <th
                            className="sortable contracts-page__th-contractor"
                            rowSpan={2}
                            onClick={() => handleSort('contractor')}
                            role="button"
                          >
                            Контрагент
                          </th>
                          <th
                            className="sortable contracts-page__th-number"
                            rowSpan={2}
                            onClick={() => handleSort('contract_number')}
                            role="button"
                          >
                            Номер договора
                          </th>
                          <th
                            className="sortable contracts-page__th-address"
                            rowSpan={2}
                            onClick={() => handleSort('address')}
                            role="button"
                          >
                            Адрес
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            rowSpan={2}
                            onClick={() => handleSort('manager')}
                            role="button"
                          >
                            Менеджер
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            rowSpan={2}
                            onClick={() => handleSort('projectLead')}
                            role="button"
                          >
                            РП
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            rowSpan={2}
                            onClick={() => handleSort('start_date')}
                            role="button"
                          >
                            Начало
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            rowSpan={2}
                            onClick={() => handleSort('end_date')}
                            role="button"
                          >
                            Срок
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            rowSpan={2}
                            onClick={() => handleSort('expense_closing_date')}
                            role="button"
                          >
                            Факт закрытие расходов
                          </th>
                          <th
                            className="sortable in-progress-contracts-page__th-overdue"
                            rowSpan={2}
                            onClick={() => handleSort('overdue')}
                            role="button"
                          >
                            Просрочка
                          </th>
                          <th
                            className="sortable col-num contracts-page__th-sum"
                            rowSpan={2}
                            onClick={() => handleSort('total_amount')}
                            role="button"
                          >
                            Сумма
                          </th>
                          <th
                            className="sortable contracts-page__th-status"
                            rowSpan={2}
                            onClick={() => handleSort('contract_status')}
                            role="button"
                          >
                            Статус
                          </th>
                          <th className="in-progress-smo-ul-page__th-closing-group" colSpan={2}>
                            Условия закрытия
                          </th>
                        </tr>
                        <tr>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing closing-contracts-page__th-check"
                            onClick={() => handleSort('fully_paid')}
                            role="button"
                          >
                            Полная оплата
                          </th>
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing closing-contracts-page__th-check"
                            onClick={() => handleSort('expenses_closed')}
                            role="button"
                          >
                            Все этапы закрыты бухгалтерией
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedContracts.length === 0 ? (
                          <tr>
                            <td colSpan={tableColSpan} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedContracts.map((contract, index) => {
                            const level = contractLevel(contract);
                            const deadline = level === 1 ? getDeadline(contract) : null;
                            const prev = index > 0 ? sortedContracts[index - 1] : null;
                            const isNewMainContractGroup =
                              !prev || (prev.main_contract_id || prev.id) !== (contract.main_contract_id || contract.id);
                            return (
                              <tr
                                key={contract.id}
                                className={`clickable-row ${isNewMainContractGroup ? 'contracts-page__row--new-main' : ''} ${level === 2 ? 'contracts-page__row--supplementary' : 'contracts-page__row--main'}`}
                                onClick={(e) => {
                                  if (e.ctrlKey || e.metaKey) {
                                    return;
                                  }
                                  navigate(contractDetailPath(contract));
                                }}
                              >
                                {canCloseFromAccounting ? (
                                  <td
                                    className="closing-tasks-page__td-check"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {level === 1 ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.has(contract.id)}
                                        onChange={() => toggleRowSelected(contract.id)}
                                        aria-label={`Выбрать договор ${contract.contract_number}`}
                                      />
                                    ) : null}
                                  </td>
                                ) : null}
                                <td className="contracts-page__td-contractor">
                                  {level === 2 ? (
                                    <span
                                      className="contracts-page__tree-mark"
                                      title={`Доп. соглашение к договору ${contract.main_contract_number}`}
                                      aria-label={`Доп. соглашение к договору ${contract.main_contract_number}`}
                                    >
                                      └──
                                    </span>
                                  ) : (
                                    <div className="contracts-page__clamp-2">{contract.contractor_name}</div>
                                  )}
                                </td>
                                <td className="contracts-page__td-number">
                                  <div className="contracts-page__clamp-2">
                                    <strong>{contract.contract_number}</strong>
                                  </div>
                                </td>
                                <td className="contracts-page__td-address">
                                  <div className="contracts-page__clamp-2">
                                    {level === 2 ? '—' : formatContractAddressCell(contract)}
                                  </div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">
                                    {level === 2 ? '—' : contract.manager_name}
                                  </div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">
                                    {level === 2 ? '—' : contract.project_lead_name}
                                  </div>
                                </td>
                                <td className="contracts-page__td-date">
                                  {contract.start_date ? formatDateShort(contract.start_date) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {contract.end_date ? formatDateShort(contract.end_date) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {contract.expense_closing_date
                                    ? formatDateShort(contract.expense_closing_date)
                                    : '—'}
                                </td>
                                <td
                                  className={`in-progress-contracts-page__td-overdue${deadline?.overdue ? ' in-progress-contracts-page__cell--overdue' : ''}`}
                                >
                                  {deadline?.label ? (
                                    <div className="contracts-page__clamp-2 in-progress-contracts-page__deadline">
                                      <span className="in-progress-contracts-page__deadline-label">
                                        {deadline.label.toLowerCase()}
                                      </span>
                                      <span>{formatDeadlineCompact(deadline)}</span>
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="col-num contracts-page__td-sum">
                                  <strong>{formatContractSumDisplay(contract.total_amount ?? 0)}</strong>
                                </td>
                                <td className="contracts-page__td-status">
                                  {level === 2 ? '—' : contract.contract_status || '—'}
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing closing-contracts-page__td-check">
                                  <ClosingStatusIcon status={contract.fully_paid.status} />
                                </td>
                                <td className="in-progress-smo-ul-page__td-closing closing-contracts-page__td-check">
                                  <ClosingStatusIcon
                                    status={contract.expenses_closed.status}
                                    closingDate={contract.expenses_closed.closing_date}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          {canCloseFromAccounting ? <td className="closing-tasks-page__td-check" /> : null}
                          <td>Итого: {sortedContracts.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num"><strong>{formatContractSumDisplay(totalFilteredAmount)}</strong></td>
                          <td />
                          <td className="in-progress-smo-ul-page__td-closing" />
                          <td className="in-progress-smo-ul-page__td-closing" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default ClosingContractsPage;
